/**
 * GmcConnectPanel – Google Merchant Center integration panel.
 * OAuth flow, merchant account selection, product status fetch.
 */
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Loader2,
  ShoppingCart,
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

/** Odpowiedz /api/integrations/google-merchant/insights. */
type GmcInsights = {
  context?: string;
  productStatuses: Array<{
    productId?: string;
    title?: string;
    approvalStatus?: string;
    issues?: Array<{ description?: string }>;
  }>;
};

interface MerchantAccount {
  id: string;
  name: string;
}
interface GmcStatus {
  connected: boolean;
  googleEmail?: string;
  merchantId?: string;
  merchantName?: string;
  merchantAccounts?: MerchantAccount[];
}
interface GmcConnectPanelProps {
  onInsightsLoaded?: (context: string) => void;
  forUserId?: string;
}

const GmcLogo = () => (
  <div className="w-6 h-6 rounded bg-[#4285F4] flex items-center justify-center">
    <ShoppingCart className="w-3.5 h-3.5 text-white" />
  </div>
);

export default function GmcConnectPanel({ onInsightsLoaded, forUserId }: GmcConnectPanelProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [insightsData, setInsightsData] = useState<GmcInsights | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const fuParam = forUserId ? `?forUserId=${forUserId}` : "";

  const { data: status, isLoading } = useQuery<GmcStatus>({
    queryKey: ["gmc-integration-status", forUserId],
    queryFn: () => apiRequest("GET", `/api/integrations/google-merchant/status${fuParam}`),
    staleTime: 30_000,
  });

  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (event.data?.type === "GMC_OAUTH_SUCCESS") {
        queryClient.invalidateQueries({ queryKey: ["gmc-integration-status"] });
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [queryClient]);

  const connectMutation = useMutation({
    mutationFn: () => apiRequest("GET", "/api/integrations/google-merchant/connect"),
    onSuccess: (data: { url: string }) => {
      const w = 600,
        h = 700;
      const left = window.screenX + (window.outerWidth - w) / 2;
      const top = window.screenY + (window.outerHeight - h) / 2;
      const popup = window.open(
        data.url,
        "gmc_oauth",
        `width=${w},height=${h},left=${left},top=${top},toolbar=no,menubar=no`
      );
      const check = setInterval(() => {
        if (popup?.closed) {
          clearInterval(check);
          queryClient.invalidateQueries({ queryKey: ["gmc-integration-status"] });
        }
      }, 500);
    },
  });

  const disconnectMutation = useMutation({
    mutationFn: () => apiRequest("POST", "/api/integrations/google-merchant/disconnect"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["gmc-integration-status"] });
      setInsightsData(null);
    },
  });

  const selectAccountMutation = useMutation({
    mutationFn: (acc: { merchantId: string; merchantName: string }) =>
      apiRequest("POST", "/api/integrations/google-merchant/select-account", acc),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["gmc-integration-status"] }),
  });

  const insightsMutation = useMutation({
    mutationFn: () => apiRequest("POST", "/api/integrations/google-merchant/insights"),
    onSuccess: (data: GmcInsights) => {
      setInsightsData(data);
      onInsightsLoaded?.(data.context ?? "");
    },
  });

  if (isLoading) {
    return (
      <div className="p-3 bg-white/[0.04] border border-white/[0.08] rounded-lg flex items-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin text-blue-400" />
        <span className="text-sm text-white/60">{t("connectPanels.gmc.checking")}</span>
      </div>
    );
  }

  if (!status?.connected) {
    return (
      <div className="p-3 bg-white/[0.04] border border-white/[0.08] rounded-lg space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <GmcLogo />
            <span className="text-sm font-medium text-white">Google Merchant Center</span>
            <Badge variant="outline" className="text-xs border-white/[0.1] text-white/60">
              {t("connectPanels.common.notConnected")}
            </Badge>
          </div>
          <Button
            size="sm"
            onClick={() => connectMutation.mutate()}
            disabled={connectMutation.isPending}
            className="bg-[#4285F4] hover:bg-[#3367D6] text-white text-xs"
          >
            {connectMutation.isPending ? (
              <>
                <Loader2 className="h-3 w-3 animate-spin mr-1" />{" "}
                {t("connectPanels.common.connecting")}
              </>
            ) : (
              t("connectPanels.gmc.connect")
            )}
          </Button>
        </div>
        <p className="text-xs text-white/40">{t("connectPanels.gmc.hint")}</p>
      </div>
    );
  }

  const accounts = status.merchantAccounts || [];

  return (
    <div className="p-3 bg-white/[0.04] border border-[#4285F4]/30 rounded-lg space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <GmcLogo />
          <span className="text-sm font-medium text-white">Google Merchant Center</span>
          <Badge className="text-xs bg-green-600/20 text-green-400 border-green-500/50">
            {t("connectPanels.common.connected")}
            {status.googleEmail ? ` · ${status.googleEmail}` : ""}
          </Badge>
        </div>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => disconnectMutation.mutate()}
          disabled={disconnectMutation.isPending}
          className="text-xs text-red-400 hover:text-red-300 hover:bg-red-500/10"
        >
          {t("connectPanels.common.disconnect")}
        </Button>
      </div>

      {accounts.length > 1 && (
        <div className="flex items-center gap-2">
          <span className="text-xs text-white/60 whitespace-nowrap">
            {t("connectPanels.gmc.account")}:
          </span>
          <Select
            value={status.merchantId || ""}
            onValueChange={(val) => {
              const acc = accounts.find((a) => a.id === val);
              if (acc) selectAccountMutation.mutate({ merchantId: acc.id, merchantName: acc.name });
            }}
          >
            <SelectTrigger className="h-8 text-xs bg-[#030407] border-white/[0.1] text-white">
              <SelectValue placeholder={t("connectPanels.gmc.accountPlaceholder")} />
            </SelectTrigger>
            <SelectContent>
              {accounts.map((a) => (
                <SelectItem key={a.id} value={a.id}>
                  {a.name} ({a.id})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      {accounts.length === 1 && (
        <div className="text-xs text-white/60">
          {t("connectPanels.gmc.account")}:{" "}
          <span className="text-white font-medium">{accounts[0].name}</span>
        </div>
      )}

      {status.merchantId && (
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            onClick={() => insightsMutation.mutate()}
            disabled={insightsMutation.isPending}
            className="bg-[#4285F4] hover:bg-[#3367D6] text-white text-xs"
          >
            {insightsMutation.isPending ? (
              <>
                <Loader2 className="h-3 w-3 animate-spin mr-1" />{" "}
                {t("connectPanels.common.fetching")}
              </>
            ) : (
              t("connectPanels.common.fetch")
            )}
          </Button>
        </div>
      )}

      {insightsData && insightsData.productStatuses && (
        <>
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-3">
              <div className="flex items-center gap-2">
                <ShoppingCart className="h-3.5 w-3.5 text-blue-400" />
                <span className="text-[11px] text-white/60 uppercase">
                  {t("connectPanels.common.products")}
                </span>
              </div>
              <div className="text-xl font-bold text-blue-400 mt-1">
                {insightsData.productStatuses.length}
              </div>
            </div>
            <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-3">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />
                <span className="text-[11px] text-white/60 uppercase">
                  {t("connectPanels.gmc.approved")}
                </span>
              </div>
              <div className="text-xl font-bold text-emerald-400 mt-1">
                {insightsData.productStatuses.filter((p) => p.approvalStatus === "approved").length}
              </div>
            </div>
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-3">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-3.5 w-3.5 text-amber-400" />
                <span className="text-[11px] text-white/60 uppercase">
                  {t("connectPanels.gmc.problems")}
                </span>
              </div>
              <div className="text-xl font-bold text-amber-400 mt-1">
                {insightsData.productStatuses.filter((p) => p.approvalStatus !== "approved").length}
              </div>
            </div>
          </div>

          {insightsData.productStatuses.length > 0 && (
            <div>
              <button
                onClick={() => setShowDetails(!showDetails)}
                className="flex items-center gap-1.5 text-xs text-white/60 hover:text-white/70 transition-colors w-full"
              >
                {showDetails ? (
                  <ChevronUp className="h-3.5 w-3.5" />
                ) : (
                  <ChevronDown className="h-3.5 w-3.5" />
                )}
                <span className="font-medium">
                  {t("connectPanels.gmc.products", {
                    count: Math.min(insightsData.productStatuses.length, 15),
                  })}
                </span>
              </button>
              {showDetails && (
                <div className="mt-2 rounded-lg border border-white/[0.08] overflow-hidden max-h-48 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-[#0a0c10]/80 text-white/60">
                        <th className="text-left p-2 font-medium">
                          {t("connectPanels.common.product")}
                        </th>
                        <th className="text-right p-2 font-medium">
                          {t("connectPanels.common.status")}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {insightsData.productStatuses.slice(0, 15).map((p, i) => (
                        <tr key={i} className="border-t border-white/[0.06]">
                          <td className="p-2 text-white max-w-[250px] truncate">
                            {p.title || p.productId}
                          </td>
                          <td className="p-2 text-right">
                            {p.approvalStatus === "approved" ? (
                              <span className="text-emerald-400">OK</span>
                            ) : (
                              <span className="text-amber-400">{p.approvalStatus}</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          <div className="flex items-center gap-2">
            <Badge className="text-xs bg-green-600/20 text-green-400 border-green-500/50">
              {t("connectPanels.common.dataLoaded")}
            </Badge>
            <span className="text-xs text-white/40">{t("connectPanels.gmc.contextAttached")}</span>
          </div>
        </>
      )}
    </div>
  );
}
